import type { Project } from '@atlas/api-contract';
import { useMemo } from 'react';
import { toast } from 'sonner';

import { ProjectFormDialog } from './project-form-dialog';
import {
  toUpdateProjectPayload,
  type ProjectFormValues,
} from './project-schemas';
import { useUpdateProject } from './projects-queries';

export interface EditProjectDialogProps {
  /** The project being edited, or `null` when nothing is. */
  project: Project | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Rename a project or rewrite its description.
 *
 * Renaming hits the same unique index a create does, so this inherits the
 * inline 409 from {@link ProjectFormDialog} rather than restating it.
 */
export const EditProjectDialog = ({
  project,
  onOpenChange,
}: EditProjectDialogProps) => {
  const updateProject = useUpdateProject();

  const defaultValues = useMemo<ProjectFormValues>(
    () => ({
      name: project?.name ?? '',
      description: project?.description ?? '',
    }),
    [project],
  );

  const handleSubmit = async (values: ProjectFormValues): Promise<boolean> => {
    if (project === null) {
      return false;
    }

    const updated = await updateProject.mutateAsync({
      projectId: project.id,
      payload: toUpdateProjectPayload(values),
    });

    toast.success(`Saved “${updated.name}”.`);
    return true;
  };

  return (
    <ProjectFormDialog
      open={project !== null}
      onOpenChange={onOpenChange}
      title="Edit project"
      description="Renaming a project does not affect the prototypes inside it."
      submitLabel="Save changes"
      pendingLabel="Saving…"
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
    />
  );
};
